import { Chip } from "@legal-500/legal500-design-system";
import styles from "./AwardsCells.module.css";

/**
 * FALLBACK LABELLING.
 *
 * The list endpoint returns raw taxonomy country ids and nothing else; the real
 * names are resolved client-side from the region-countries tree by
 * `useCountryNames()`, which the page passes in as `getLabel`. This function is
 * what remains when that lookup has no answer — the tree is still in flight, or
 * taxonomy is down, or the id simply is not in the tree. A chip then degrades to
 * a labelled id ("Country 826") rather than a bare number, which is the house
 * fallback (`RegionChip` renders `${kind} ${id}` the same way).
 *
 * `useCountryNames` calls this for its own unknown-id case, so the fallback
 * format has exactly one definition.
 */
export function jurisdictionLabel(countryId: number): string {
  return `Country ${countryId}`;
}

type JurisdictionChipsProps = {
  countryIds: number[];
  /** Overrides the `Country ${id}` fallback once real names are available. */
  getLabel?: (countryId: number) => string;
};

export function JurisdictionChips({
  countryIds,
  getLabel = jurisdictionLabel,
}: JurisdictionChipsProps) {
  // An empty array is legitimate — a multi-country series resolves to no single
  // country — so the cell stays empty rather than claiming a missing value with
  // an em dash.
  if (countryIds.length === 0) {
    return null;
  }

  return (
    <div className={styles.jurisdictionChipList}>
      {countryIds.map(countryId => (
        // `size="small" variant="outlined"` with no tonal class: the tonal chip
        // classes in src/styles/chips.module.css force `text-transform:
        // lowercase`, which would render "United Kingdom" as "united kingdom".
        <Chip key={countryId} size="small" variant="outlined">
          {getLabel(countryId)}
        </Chip>
      ))}
    </div>
  );
}
